import { useTranslation } from "react-i18next";
import { Button } from "@carbon/react";
import {
  NotificationInterface,
  NotificationsPaginatedResponse,
} from "../../types/notification.type";
import "./notifications.scss";

/**
 * The notifications feed inside the header bell panel — rebuilt on Carbon tokens (colours, type,
 * spacing) and Carbon `Button`s, replacing the pre-Carbon Tailwind list. It renders in the dark
 * g100 header panel, so every colour comes from a `--cds-*` token and resolves to that theme (dark
 * items, light text, a Blue-60 unread dot) — the Carbon global-header notifications look. Data,
 * pagination and click behaviour are unchanged.
 */
const Notifications = ({
  notifications,
  handleNotificationClick,
  notificationsPage,
  setNotificationsPage,
}: {
  notifications: NotificationsPaginatedResponse;
  handleNotificationClick: (notification: NotificationInterface) => void;
  notificationsPage: number;
  setNotificationsPage: (page: number) => void;
}) => {
  const { t } = useTranslation();
  const hasItems = Boolean(notifications && notifications.totalElements > 0);

  return (
    <div className="notifications">
      {hasItems ? (
        <ul className="notifications__list">
          {notifications.content.map((notification) => (
            <li key={notification.id}>
              <button
                type="button"
                className={`notifications__item${
                  notification.read ? " notifications__item--read" : ""
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <span className="notifications__item-head">
                  <span className="notifications__item-title">
                    {notification.titleKey
                      ? t(notification.titleKey, notification.parameters)
                      : t(
                          `notifications.event.${notification.eventName}`,
                          notification.eventName
                        )}
                  </span>
                  {!notification.read && (
                    <span
                      className="notifications__item-dot"
                      aria-hidden="true"
                    />
                  )}
                </span>
                <span className="notifications__item-message">
                  {notification.messageKey
                    ? t(notification.messageKey, notification.parameters)
                    : notification.message}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="notifications__empty">
          {t("notification.empty", "No notifications")}
        </p>
      )}

      {hasItems && notifications.totalPages > 1 && (
        <div className="notifications__pager">
          <Button
            kind="ghost"
            size="sm"
            disabled={notificationsPage === 0}
            onClick={() => setNotificationsPage(notificationsPage - 1)}
          >
            {t("notification.previous", "Previous")}
          </Button>
          <span className="notifications__pager-count">
            {notificationsPage + 1} / {notifications.totalPages}
          </span>
          {/* Both controls are always rendered so they stay visible above the scrolling list;
              Next is disabled on the last page rather than hidden, keeping the footer stable. */}
          <Button
            kind="ghost"
            size="sm"
            disabled={notificationsPage >= notifications.totalPages - 1}
            onClick={() => setNotificationsPage(notificationsPage + 1)}
          >
            {t("notification.next", "Next")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Notifications;
